import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import {
  Section,
  SimpleDataTable,
  PrintDoc,
  DangerousWrapper,
  getFootnotes,
  DecorativeLink,
  PageHeader,
  TableofContents,
  ColoredHeading,
  CompHeading
} from '@budget-monorepo/shared-ui';
import kebabCase from 'lodash.kebabcase';
import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';


const FinancialStatementsPage = (props) => {
  const { data } = props;
  const { content, site, download } = data;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = content.parent;
  const layoutProps = {
    activeIndex: 0,
    above: () => <PageHeader title={content.title} category={content.budgetSection} />,
    pageTitle: content.title,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime
  };
  // metadata for the page
  const metaTitle = content.title;
  const metaDescription = `${content.budgetSection}, three years of Massachusetts financial statements.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        <Section>
          <DecorativeLink
          /* Create a reference link to the financial statements summary file */
            href={download.publicURL}
            text={`Download ${content.budgetSection} ${content.title}`}
            showFileIcon
          />
        </Section>
        <TableofContents heading={<ColoredHeading text="Financial Statements" />}>
          {content.sections.map((section, index) => (
            <DecorativeLink
              info={`Go to the ${section.title} section`}
              text={section.title}
              href={`#${kebabCase(section.title)}`}
              // eslint-disable-next-line react/no-array-index-key
              key={`toc-link-${index}`}
            />
          ))}
        </TableofContents>
        {content.sections.map((section, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div className="ma__section-wrapper" id={kebabCase(section.title)} key={`fiscal-statements.sections.section.${index}`}>
            <CompHeading title={section.title} />
            <DangerousWrapper rawHtml={section.summary} htmlTag="p" />
            <SimpleDataTable title={`${content.title} ${section.title}`} table={section.financialStatementsTable} maxColWidth={400} mantissa={1} />
          </div>
        ))}
        {content.footnotes && (
        <Section>
          {getFootnotes(content.footnotes)}
        </Section>
        )}
      </Layout>
      <PrintDoc doc={download.publicURL} />
    </Fragment>
  );
};

FinancialStatementsPage.propTypes = {
  data: PropTypes.object
};

export default FinancialStatementsPage;

export const query = graphql`
  query financialStatementsJson {
    site {
      ...SiteMetadata
    }
    # Fetch the url of the financial statements pdf file for an in page download link.
    # If the file name changes, the query here ( eq: "fy2022h1_tab5.pdf") will need to be updated.
    download: file(base: { eq: "fy2022h1_tab5.pdf" }) {
      base
      publicURL
    }
    content: pagesJson(fields: { slug: { eq: "/financial-statements/" } }) {
      id
      title
      budgetSection
      sections {
        title
        summary
        financialStatementsTable {
          headers {
            name
            type
            align
            value
            footnote
          }
          data {
            budgetedFunds
            rowType
            allFunds
            cnsFunds
            # general
            # transFund
            # localCapitalProjects
            # gamingLocalAid
            # education
            # gamingEconomicDevelopment
            # marijuanaRegulation
            # undergroundStorageTank
            nonCnsFunds
          }
        }
      }
      footnotes {
        index
        text
      }
      # Get the last modified timestamp for the file powering the page
      ...PagesJsonDataTimeStamp
    }
  }
`;
